import React from 'react';
import { Nav } from 'react-bootstrap';
import './Sidebar.css';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../../Assets/icon.png';

interface NavigationItem {
    path: string;
    label: string;
    icon: string;
    exact?: boolean;
}

const Sidebar: React.FC = () => {
    const location = useLocation();

    const getTrailIdFromPath = (pathname: string): string => {
        const segments = pathname.split('/').filter(Boolean);

        if (segments[0] === 'trail') {
            if (segments[1] === 'edit' && segments[2]) {
                return segments[2]; // /trail/edit/123
            } else if (segments[1] && segments[1] !== 'edit') {
                return segments[1]; // /trail/123/reviews or /trail/123
            }
        } else if (segments[0] === 'waypoints' && segments[1]) {
            return segments[1]; // /waypoints/123
        }

        return '';
    };

    const navigationItems: NavigationItem[] = [
        {
            path: '/',
            label: 'Dashboard',
            icon: 'fas fa-tachometer-alt',
            exact: true
        },
        {
            path: '/trails',
            label: 'Trails',
            icon: 'fas fa-route'
        },
        {
            path: '/routes',
            label: 'User Routes',
            icon: 'fas fa-map-marked-alt'
        },
        {
            path: '/users',
            label: 'Users',
            icon: 'fas fa-users'
        },
        {
            path: '/reviews',
            label: 'Reviews',
            icon: 'fas fa-star'
        },
        {
            path: '/tags',
            label: 'Tag Management',
            icon: 'fas fa-tags'
        },
        {
            path: '/locations',
            label: 'Locations',
            icon: 'fas fa-globe'
        },
        {
            path: '/jta-regions',
            label: 'JTA Regions',
            icon: 'fas fa-mountain'
        },
        {
            path: '/jta-content',
            label: 'JTA Content',
            icon: 'fas fa-file-lines'
        },
        {
            path: '/onboarding-questions',
            label: 'Onboarding Questions',
            icon: 'fas fa-question-circle'
        },
        {
            path: '/notifications',
            label: 'Notifications',
            icon: 'fas fa-bell'
        },
        {
            path: '/statistics',
            label: 'Statistics',
            icon: 'fas fa-chart-bar'
        },
        {
            path: '/versions',
            label: 'Version Management',
            icon: 'fas fa-code-branch'
        }
    ];

    const isActive = (item: NavigationItem): boolean => {
        if (item.exact) {
            return location.pathname === item.path;
        }
        return location.pathname.startsWith(item.path);
    };

    const isTrailDetailPage = location.pathname.includes('/trail/');

    return (
        <div className="admin-sidebar">
            <div className="sidebar-header">
                <div className="sidebar-logo">
                    <img
                        src={logo}
                        alt="Darb Logo"
                        className="logo-image"
                        width="40"
                        height="40"
                    />
                    <span className="logo-text">Darb</span>
                </div>
            </div>

            <Nav className="sidebar-nav flex-column">
                {navigationItems.map((item) => (
                    <Nav.Item key={item.path} className="sidebar-nav-item">
                        <Nav.Link
                            as={Link}
                            to={item.path}
                            className={`sidebar-nav-link ${isActive(item) ? 'active' : ''}`}
                        >
                            <i className={`${item.icon} sidebar-nav-icon`}></i>
                            <span className="sidebar-nav-text">{item.label}</span>
                        </Nav.Link>
                    </Nav.Item>
                ))}

                {isTrailDetailPage && (
                    <>
                        <hr className="sidebar-divider" />
                        <div className="sidebar-section-title">Trail Management</div>
                        <Nav.Item className="sidebar-nav-item">
                            <Nav.Link
                                as={Link}
                                to={`/waypoints/${getTrailIdFromPath(location.pathname)}`}
                                className="sidebar-nav-link sidebar-sub-link"
                            >
                                <i className="fas fa-map-marker-alt sidebar-nav-icon"></i>
                                <span className="sidebar-nav-text">Waypoints</span>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="sidebar-nav-item">
                            <Nav.Link
                                as={Link}
                                to={`/trail/${getTrailIdFromPath(location.pathname)}/reviews`}
                                className="sidebar-nav-link sidebar-sub-link"
                            >
                                <i className="fas fa-star sidebar-nav-icon"></i>
                                <span className="sidebar-nav-text">Reviews</span>
                            </Nav.Link>
                        </Nav.Item>
                    </>
                )}
            </Nav>
        </div>
    );
};

export default Sidebar;
